import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@nexus/ui";
import { SetNameSchema } from "@nexus/contracts";
import { useTranslation } from "react-i18next";

import { getUserFacingApiMessage } from "@/lib/api-client";
import { useCreateUserGeneratedPersonaOpportunitySet } from "@/lib/queries";

export interface CreateUserGeneratedSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (set: { id: string; name: string }) => void;
}

/** Name-only container for imported and custom scripts. Does not generate opportunities. */
export function CreateUserGeneratedSetDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateUserGeneratedSetDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = React.useState("");
  const createSet = useCreateUserGeneratedPersonaOpportunitySet();
  const nameError = name.trim().length === 0 ? null : SetNameSchema.safeParse(name).error?.issues[0]?.message;
  const canSubmit = SetNameSchema.safeParse(name).success && !createSet.isPending;
  const createSetError = createSet.error
    ? getUserFacingApiMessage(createSet.error, (message) => message)
    : null;
  const resetCreateSet = createSet.reset;

  React.useEffect(() => {
    if (open) return;
    setName("");
    resetCreateSet();
  }, [open, resetCreateSet]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = SetNameSchema.safeParse(name);
    if (!parsed.success || createSet.isPending) return;
    createSet.mutate(parsed.data, {
      onSuccess: (set) => {
        onCreated(set);
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {t("personaOpportunities.hub.actions.userGeneratedTitle", "Name this set")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "personaOpportunities.hub.actions.userGeneratedDescription",
                "A place to import scripts or write your own. It does not generate opportunities.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="user-generated-set-name">
              {t("personaOpportunities.hub.actions.userGeneratedName", "Name")}
            </Label>
            <Input
              id="user-generated-set-name"
              className="mt-1.5"
              value={name}
              autoFocus
              disabled={createSet.isPending}
              placeholder={t(
                "personaOpportunities.hub.actions.userGeneratedPlaceholder",
                "Set name",
              )}
              onChange={(event) => setName(event.target.value)}
            />
            {nameError ? <p className="mt-1.5 text-xs text-destructive">{nameError}</p> : null}
            {createSetError ? (
              <p role="alert" className="mt-1.5 text-xs text-destructive">
                {createSetError}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={createSet.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {createSet.isPending
                ? t("common.creating", "Creating…")
                : t("personaOpportunities.hub.actions.createSet", "Create Set")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
